import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/useAuthStore';
import { useEmpresaRolePermissions } from '../../hooks/useEmpresaRolePermissions';
import { fechaEnBogota } from '../../lib/cierre/bogotaDate';
import {
  getOrCreateCierreDiario,
  getIngresosDia,
  getGastosDia,
  lockCierreDiario,
  unlockCierreDiario,
  updateSaldosIniciales,
} from '../../lib/cierre/queries';
import { aggregateToBalance, computeSaldoFinal, formatCOP, totalBalance } from '../../lib/cierre/calculations';
import { emptyBalance, rowToBalanceInicial, type BalanceByMethod, type CierreDiarioRow } from '../../lib/cierre/types';
import {
  getEmpresaMoneda,
  getMonedasAceptadas,
  isEmpresaMultiMoneda,
} from '../../utils/empresaMoneda';
import { BalanceGrid } from './BalanceGrid';
import { CierreStatusBadge } from './CierreStatusBadge';
import { GastoQuickAddCierre } from './GastoQuickAddCierre';

export const CierreDiarioView: React.FC = () => {
  const navigate = useNavigate();
  const { fecha: fechaParam } = useParams();
  const fecha = fechaParam && fechaParam.length > 0 ? fechaParam : fechaEnBogota();
  const { currentEmpresa, usuario } = useAuthStore();
  const { isAdmin } = useEmpresaRolePermissions();
  const empresaId = currentEmpresa?.id;

  const [cierre, setCierre] = useState<CierreDiarioRow | null>(null);
  const [ingresos, setIngresos] = useState(emptyBalance());
  const [gastos, setGastos] = useState(emptyBalance());
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [editInicial, setEditInicial] = useState(false);
  const [draftInicial, setDraftInicial] = useState<BalanceByMethod>(emptyBalance());
  const monedasAceptadas = getMonedasAceptadas(currentEmpresa ?? null);
  const monedaPrincipal = getEmpresaMoneda(currentEmpresa ?? null);
  const [monedaVista, setMonedaVista] = useState(monedaPrincipal);

  useEffect(() => {
    setMonedaVista(monedaPrincipal);
  }, [monedaPrincipal]);

  const load = useCallback(async () => {
    if (!empresaId) return;
    setErr(null);
    try {
      const row = await getOrCreateCierreDiario(supabase, empresaId, fecha, usuario?.id);
      setCierre(row);
      const [ingLines, gasLines] = await Promise.all([
        getIngresosDia(supabase, empresaId, fecha),
        getGastosDia(supabase, empresaId, fecha),
      ]);
      setIngresos(aggregateToBalance(ingLines));
      setGastos(aggregateToBalance(gasLines));
    } catch (e) {
      console.error(e);
      setErr(e instanceof Error ? e.message : 'Error al cargar cierre');
    } finally {
      setLoading(false);
    }
  }, [empresaId, fecha, usuario?.id]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  useEffect(() => {
    const id = window.setInterval(() => void load(), 60_000);
    return () => clearInterval(id);
  }, [load]);

  if (!empresaId) {
    return <p className="text-gray-600">Selecciona una empresa.</p>;
  }

  if (loading && !cierre) {
    return <p className="text-gray-500">Cargando cierre…</p>;
  }

  if (err) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
        {err}
        <p className="mt-2 text-sm">
          Si acabas de desplegar el proyecto, aplica la migración Supabase{' '}
          <code className="rounded bg-red-100 px-1">20260410140000_cierre_caja_module.sql</code>.
        </p>
      </div>
    );
  }

  if (!cierre) return null;

  const inicial = rowToBalanceInicial(cierre);
  const finalCalc = computeSaldoFinal(inicial, ingresos, gastos);
  const cerrado = cierre.status === 'cerrado';
  const readOnly = cerrado;

  const guardarInicial = async () => {
    if (!usuario?.id) return;
    try {
      await updateSaldosIniciales(supabase, cierre.id, draftInicial, usuario.id);
      setEditInicial(false);
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Error al guardar');
    }
  };

  const cerrarDia = async () => {
    if (!usuario?.id) return;
    if (!window.confirm('¿Cerrar este día? No podrás editar saldos hasta desbloquear (solo admin).')) return;
    try {
      await lockCierreDiario(supabase, cierre.id, finalCalc, usuario.id);
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Error al cerrar');
    }
  };

  const desbloquear = async () => {
    if (!usuario?.id) return;
    if (!window.confirm('¿Desbloquear este cierre?')) return;
    try {
      await unlockCierreDiario(supabase, cierre.id, empresaId, usuario.id);
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Error al desbloquear');
    }
  };

  const monedasAceptadasList = monedasAceptadas;
  const cierreMultimonedaPendiente = isEmpresaMultiMoneda(currentEmpresa ?? null);
  const mostrarDatosOperativos = monedaVista === monedaPrincipal;

  return (
    <div className="space-y-6">
      {cierreMultimonedaPendiente && (
        <div className="flex flex-wrap gap-2">
          {monedasAceptadasList.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMonedaVista(m)}
              className={`rounded-full px-3 py-1 text-sm font-medium ${
                monedaVista === m
                  ? 'bg-[#1A56DB] text-white'
                  : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      )}
      {cierreMultimonedaPendiente && !mostrarDatosOperativos && (
        <div
          className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900"
          role="status"
        >
          Aún no hay movimientos desglosados en {monedaVista}. Los ingresos del cierre se registran hoy en{' '}
          {monedaPrincipal}. Usa esta pestaña para conciliar manualmente cuando habilitemos el desglose por moneda en
          Supabase.
        </div>
      )}
      {cierreMultimonedaPendiente && mostrarDatosOperativos && (
        <div
          className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          role="status"
        >
          Esta empresa opera en {monedasAceptadasList.join(', ')}. Los totales mostrados corresponden a{' '}
          {monedaPrincipal} (moneda principal) hasta que el RPC de cierre agrupe por moneda.
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0B1F4A]">Cierre diario</h1>
          <p className="text-sm text-gray-600">Fecha operativa (Bogotá): {fecha}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <CierreStatusBadge status={cierre.status} lockedAt={cierre.locked_at} />
          <input
            type="date"
            className="rounded border border-gray-300 px-2 py-1 text-sm"
            value={fecha}
            onChange={(e) => {
              const v = e.target.value;
              if (v) navigate(`/cierre/dia/${v}`);
            }}
          />
          <Link to="/cierre" className="text-sm text-[#1A56DB] hover:underline">
            Hoy
          </Link>
        </div>
      </div>

      <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold text-gray-900">A · Saldos iniciales</h2>
          {isAdmin && !readOnly ? (
            editInicial ? (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={guardarInicial}
                  className="rounded bg-[#1A56DB] px-3 py-1 text-sm text-white"
                >
                  Guardar
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDraftInicial(inicial);
                    setEditInicial(false);
                  }}
                  className="rounded border border-gray-300 px-3 py-1 text-sm"
                >
                  Cancelar
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setDraftInicial(rowToBalanceInicial(cierre));
                  setEditInicial(true);
                }}
                className="text-sm font-medium text-[#1A56DB] hover:underline"
              >
                Editar saldo inicial
              </button>
            )
          ) : null}
        </div>
        {cierre.override_inicial ? (
          <p className="mb-2 text-xs text-amber-800">Saldo inicial ajustado manualmente.</p>
        ) : null}
        {editInicial && !readOnly ? (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {(
              [
                ['efectivo', 'Efectivo'],
                ['banco', 'Banco / transferencia'],
                ['nequi', 'Nequi'],
                ['daviplata', 'DaviPlata'],
                ['tarjeta', 'Tarjeta'],
                ['otro', 'Otro'],
              ] as const
            ).map(([k, lab]) => (
              <label key={k} className="block text-sm">
                <span className="text-gray-600">{lab}</span>
                <input
                  type="number"
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-1"
                  value={draftInicial[k]}
                  onChange={(e) =>
                    setDraftInicial((d) => ({ ...d, [k]: Math.round(parseFloat(e.target.value) || 0) }))
                  }
                />
              </label>
            ))}
          </div>
        ) : (
          <BalanceGrid balances={inicial} label="" />
        )}
        <p className="mt-2 text-sm font-semibold text-gray-800">Total inicial: {formatCOP(totalBalance(inicial))}</p>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="mb-3 text-lg font-semibold text-gray-900">
          B · Ingresos del día{mostrarDatosOperativos ? '' : ` (${monedaVista})`}
        </h2>
        {mostrarDatosOperativos ? (
          <>
            <p className="mb-2 text-xs text-gray-500">Desde facturas pagadas (historial de pagos o monto pagado). Actualiza cada 60 s.</p>
            <BalanceGrid balances={ingresos} />
          </>
        ) : (
          <p className="text-sm text-gray-500">Sin ingresos registrados en {monedaVista} para esta fecha.</p>
        )}
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="mb-3 text-lg font-semibold text-gray-900">
          C · Gastos del día{mostrarDatosOperativos ? '' : ` (${monedaVista})`}
        </h2>
        {mostrarDatosOperativos ? (
          <>
            <p className="mb-2 text-xs text-gray-500">Gastos marcados pagados con fecha del día.</p>
            <BalanceGrid balances={gastos} />
            {!readOnly ? (
              <GastoQuickAddCierre
                empresaId={empresaId}
                usuarioId={usuario?.id}
                fecha={fecha}
                empresaCtx={currentEmpresa ?? null}
                onSuccess={load}
              />
            ) : null}
          </>
        ) : (
          <p className="text-sm text-gray-500">Sin gastos registrados en {monedaVista} para esta fecha.</p>
        )}
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="mb-3 text-lg font-semibold text-gray-900">
          D · Saldos finales (calculado){mostrarDatosOperativos ? '' : ` · ${monedaVista}`}
        </h2>
        {mostrarDatosOperativos ? (
          <>
            <BalanceGrid
              columns="cierre"
              balances={finalCalc}
              inicial={inicial}
              ingresos={ingresos}
              gastos={gastos}
              highlightNegative
            />
            <p className="mt-4 text-xl font-bold text-[#0B1F4A]">Total en caja: {formatCOP(totalBalance(finalCalc))}</p>
          </>
        ) : (
          <p className="text-sm text-gray-500">Selecciona {monedaPrincipal} para ver saldos calculados.</p>
        )}
      </section>

      <div className="flex flex-wrap gap-3">
        {!readOnly ? (
          <button
            type="button"
            onClick={cerrarDia}
            className="rounded-lg bg-[#1A56DB] px-5 py-2.5 text-sm font-semibold text-white shadow"
          >
            Cerrar día
          </button>
        ) : null}
        {readOnly && isAdmin ? (
          <button
            type="button"
            onClick={desbloquear}
            className="rounded-lg border border-amber-500 px-5 py-2.5 text-sm font-semibold text-amber-900"
          >
            Desbloquear (admin)
          </button>
        ) : null}
      </div>
    </div>
  );
};
